import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {CompanyMainInformation} from '../models/api/crm/CompanyMainInformation';
import {CompanyActionSummary} from '../models/api/crm/CompanyActionSummary';
import {CompanyOpportunitySummary} from '../models/api/crm/CompanyOpportunitySummary';
import {CompanyDetailView} from '../models/api/crm/CompanyDetailView';
import {GroupMemberAssignmentSummary} from '../models/api/crm/GroupMemberAssignmentSummary';
import {CustomerCreateInput} from '../models/api/crm/CustomerCreateInput';
import {CustomerUpdateInput} from '../models/api/crm/CustomerUpdateInput';
import {CustomerDetails} from '../models/api/crm/CustomerDetails';
import {CompanyDetails} from '../models/api/crm/CompanyDetails';
import {CompanyMainContactUpdate} from '../models/api/crm/CompanyMainContactUpdate';
import {CompanyOwnerUserUpdate} from '../models/api/crm/CompanyOwnerUserUpdate';
import {CompanyWebLinksUpdate} from '../models/api/crm/CompanyWebLinksUpdate';
import {ContractCreateInput} from '../models/api/crm/ContractCreateInput';
import {ContractUpdateInput} from '../models/api/crm/ContractUpdateInput';
import {ContractDetails} from '../models/api/crm/ContractDetails';
import {ContractExperienceFeedbackCreateInput} from '../models/api/crm/ContractExperienceFeedbackCreateInput';
import {ContractExperienceFeedbackDetails} from '../models/api/crm/ContractExperienceFeedbackDetails';
import {ContractExperienceFeedbackUpdateInput} from '../models/api/crm/ContractExperienceFeedbackUpdateInput';
import {ActionCreateInput} from '../models/api/crm/ActionCreateInput';
import {ActionUpdateInput} from '../models/api/crm/ActionUpdateInput';
import {ActionDetails} from '../models/api/crm/ActionDetails';
import {CommentCreateInput} from '../models/api/crm/CommentCreateInput';
import {CommentUpdateInput} from '../models/api/crm/CommentUpdateInput';
import {CommentDetails} from '../models/api/crm/CommentDetails';
import {CrmDocumentSummary} from '../models/api/crm/CrmDocumentSummary';

@Injectable({
  providedIn: 'root'
})
export class CompanyApiService {
  private static readonly COMPANIES_URL = `${environment.apiUrl}/companies`;
  private static readonly COMPANY_ACTION_FEED_URL = `${environment.apiUrl}/companies/action-feed`;
  private static readonly COMPANY_OPPORTUNITY_FEED_URL = `${environment.apiUrl}/companies/opportunity-feed`;
  private static readonly GROUP_MEMBER_ASSIGNMENTS_URL = `${environment.apiUrl}/companies/group-member-assignments`;
  private static readonly COMPANY_DETAILS_URL = `${environment.apiUrl}/companies/{companyId}/details`;
  private static readonly COMPANY_CUSTOMERS_URL = `${environment.apiUrl}/companies/{companyId}/customers`;
  private static readonly COMPANY_CUSTOMER_URL = `${environment.apiUrl}/companies/{companyId}/customers/{customerId}`;
  private static readonly COMPANY_MAIN_CONTACT_URL = `${environment.apiUrl}/companies/{companyId}/main-contact`;
  private static readonly COMPANY_OWNER_USER_URL = `${environment.apiUrl}/companies/{companyId}/owner-user`;
  private static readonly COMPANY_WEB_LINKS_URL = `${environment.apiUrl}/companies/{companyId}/web-links`;
  private static readonly COMPANY_CONTRACTS_URL = `${environment.apiUrl}/companies/{companyId}/contracts`;
  private static readonly COMPANY_CONTRACT_URL = `${environment.apiUrl}/companies/{companyId}/contracts/{contractId}`;
  private static readonly COMPANY_CONTRACT_REX_URL = `${environment.apiUrl}/companies/{companyId}/contracts/{contractId}/rex`;
  private static readonly COMPANY_CONTRACT_REX_ITEM_URL = `${environment.apiUrl}/companies/{companyId}/contracts/{contractId}/rex/{rexId}`;
  private static readonly COMPANY_ACTIONS_URL = `${environment.apiUrl}/companies/{companyId}/actions`;
  private static readonly COMPANY_ACTION_URL = `${environment.apiUrl}/companies/{companyId}/actions/{actionId}`;
  private static readonly COMPANY_COMMENTS_URL = `${environment.apiUrl}/companies/{companyId}/comments`;
  private static readonly COMPANY_COMMENT_URL = `${environment.apiUrl}/companies/{companyId}/comments/{commentId}`;
  private static readonly COMPANY_COMMENT_DOCUMENTS_URL = `${environment.apiUrl}/companies/{companyId}/comments/{commentId}/documents`;
  private static readonly COMPANY_COMMENT_DOCUMENT_URL = `${environment.apiUrl}/companies/{companyId}/comments/{commentId}/documents/{documentId}`;
  private static readonly COMPANY_COMMENT_DOCUMENT_DOWNLOAD_URL = `${environment.apiUrl}/companies/{companyId}/comments/{commentId}/documents/{documentId}/download`;
  private static readonly COMPANY_DOCUMENTS_URL = `${environment.apiUrl}/companies/{companyId}/documents`;
  private static readonly COMPANY_DOCUMENT_URL = `${environment.apiUrl}/companies/{companyId}/documents/{documentId}`;
  private static readonly COMPANY_DOCUMENT_DOWNLOAD_URL = `${environment.apiUrl}/companies/{companyId}/documents/{documentId}/download`;

  constructor(private readonly http: HttpClient) {}

  public getCompanies(groupId: string): Observable<CompanyMainInformation[]> {
    return this.http.get<CompanyMainInformation[]>(CompanyApiService.COMPANIES_URL, {params: {groupId}});
  }

  public getCompanyActionFeed(groupId: string): Observable<CompanyActionSummary[]> {
    return this.http.get<CompanyActionSummary[]>(CompanyApiService.COMPANY_ACTION_FEED_URL, {params: {groupId}});
  }

  public getCompanyOpportunityFeed(groupId: string): Observable<CompanyOpportunitySummary[]> {
    return this.http.get<CompanyOpportunitySummary[]>(CompanyApiService.COMPANY_OPPORTUNITY_FEED_URL, {params: {groupId}});
  }

  public getGroupMemberAssignments(groupId: string): Observable<GroupMemberAssignmentSummary[]> {
    return this.http.get<GroupMemberAssignmentSummary[]>(CompanyApiService.GROUP_MEMBER_ASSIGNMENTS_URL, {params: {groupId}});
  }

  public getCompanyDetail(companyId: string, groupId: string): Observable<CompanyDetailView> {
    return this.http.get<CompanyDetailView>(
      CompanyApiService.COMPANY_DETAILS_URL.replace('{companyId}', companyId),
      {params: {groupId}}
    );
  }

  public deleteCompany(companyId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      `${CompanyApiService.COMPANIES_URL}/${companyId}`,
      {params: {groupId}}
    );
  }

  public createCustomer(companyId: string, groupId: string, payload: CustomerCreateInput): Observable<CustomerDetails> {
    return this.http.post<CustomerDetails>(
      CompanyApiService.COMPANY_CUSTOMERS_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public getCustomer(companyId: string, customerId: string, groupId: string): Observable<CustomerDetails> {
    return this.http.get<CustomerDetails>(
      CompanyApiService.COMPANY_CUSTOMER_URL
        .replace('{companyId}', companyId)
        .replace('{customerId}', customerId),
      {params: {groupId}}
    );
  }

  public updateCustomer(
    companyId: string,
    customerId: string,
    groupId: string,
    payload: CustomerUpdateInput
  ): Observable<CustomerDetails> {
    return this.http.put<CustomerDetails>(
      CompanyApiService.COMPANY_CUSTOMER_URL
        .replace('{companyId}', companyId)
        .replace('{customerId}', customerId),
      payload,
      {params: {groupId}}
    );
  }

  public deleteCustomer(companyId: string, customerId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_CUSTOMER_URL
        .replace('{companyId}', companyId)
        .replace('{customerId}', customerId),
      {params: {groupId}}
    );
  }

  public setMainContact(companyId: string, groupId: string, mainContactId: string | null): Observable<CompanyDetails> {
    const payload: CompanyMainContactUpdate = {mainContactId};
    return this.http.put<CompanyDetails>(
      CompanyApiService.COMPANY_MAIN_CONTACT_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public setOwnerUser(companyId: string, groupId: string, ownerUserId: string | null): Observable<CompanyDetails> {
    const payload: CompanyOwnerUserUpdate = {ownerUserId};
    return this.http.put<CompanyDetails>(
      CompanyApiService.COMPANY_OWNER_USER_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public updateCompanyWebLinks(companyId: string, groupId: string, payload: CompanyWebLinksUpdate): Observable<CompanyDetails> {
    return this.http.put<CompanyDetails>(
      CompanyApiService.COMPANY_WEB_LINKS_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public createContract(companyId: string, groupId: string, payload: ContractCreateInput): Observable<ContractDetails> {
    return this.http.post<ContractDetails>(
      CompanyApiService.COMPANY_CONTRACTS_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public getContract(companyId: string, contractId: string, groupId: string): Observable<ContractDetails> {
    return this.http.get<ContractDetails>(
      CompanyApiService.COMPANY_CONTRACT_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId),
      {params: {groupId}}
    );
  }

  public updateContract(
    companyId: string,
    contractId: string,
    groupId: string,
    payload: ContractUpdateInput
  ): Observable<ContractDetails> {
    return this.http.put<ContractDetails>(
      CompanyApiService.COMPANY_CONTRACT_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId),
      payload,
      {params: {groupId}}
    );
  }

  public deleteContract(companyId: string, contractId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_CONTRACT_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId),
      {params: {groupId}}
    );
  }

  public createExperienceFeedback(
    companyId: string,
    contractId: string,
    groupId: string,
    payload: ContractExperienceFeedbackCreateInput
  ): Observable<ContractExperienceFeedbackDetails> {
    return this.http.post<ContractExperienceFeedbackDetails>(
      CompanyApiService.COMPANY_CONTRACT_REX_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId),
      payload,
      {params: {groupId}}
    );
  }

  public getExperienceFeedback(
    companyId: string,
    contractId: string,
    rexId: string,
    groupId: string
  ): Observable<ContractExperienceFeedbackDetails> {
    return this.http.get<ContractExperienceFeedbackDetails>(
      CompanyApiService.COMPANY_CONTRACT_REX_ITEM_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId)
        .replace('{rexId}', rexId),
      {params: {groupId}}
    );
  }

  public updateExperienceFeedback(
    companyId: string,
    contractId: string,
    rexId: string,
    groupId: string,
    payload: ContractExperienceFeedbackUpdateInput
  ): Observable<ContractExperienceFeedbackDetails> {
    return this.http.put<ContractExperienceFeedbackDetails>(
      CompanyApiService.COMPANY_CONTRACT_REX_ITEM_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId)
        .replace('{rexId}', rexId),
      payload,
      {params: {groupId}}
    );
  }

  public deleteExperienceFeedback(companyId: string, contractId: string, rexId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_CONTRACT_REX_ITEM_URL
        .replace('{companyId}', companyId)
        .replace('{contractId}', contractId)
        .replace('{rexId}', rexId),
      {params: {groupId}}
    );
  }

  public createAction(companyId: string, groupId: string, payload: ActionCreateInput): Observable<ActionDetails> {
    return this.http.post<ActionDetails>(
      CompanyApiService.COMPANY_ACTIONS_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public getAction(companyId: string, actionId: string, groupId: string): Observable<ActionDetails> {
    return this.http.get<ActionDetails>(
      CompanyApiService.COMPANY_ACTION_URL
        .replace('{companyId}', companyId)
        .replace('{actionId}', actionId),
      {params: {groupId}}
    );
  }

  public updateAction(
    companyId: string,
    actionId: string,
    groupId: string,
    payload: ActionUpdateInput
  ): Observable<ActionDetails> {
    return this.http.put<ActionDetails>(
      CompanyApiService.COMPANY_ACTION_URL
        .replace('{companyId}', companyId)
        .replace('{actionId}', actionId),
      payload,
      {params: {groupId}}
    );
  }

  public deleteAction(companyId: string, actionId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_ACTION_URL
        .replace('{companyId}', companyId)
        .replace('{actionId}', actionId),
      {params: {groupId}}
    );
  }

  public createComment(companyId: string, groupId: string, payload: CommentCreateInput): Observable<CommentDetails> {
    return this.http.post<CommentDetails>(
      CompanyApiService.COMPANY_COMMENTS_URL.replace('{companyId}', companyId),
      payload,
      {params: {groupId}}
    );
  }

  public getComment(companyId: string, commentId: string, groupId: string): Observable<CommentDetails> {
    return this.http.get<CommentDetails>(
      CompanyApiService.COMPANY_COMMENT_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId),
      {params: {groupId}}
    );
  }

  public updateComment(
    companyId: string,
    commentId: string,
    groupId: string,
    payload: CommentUpdateInput
  ): Observable<CommentDetails> {
    return this.http.put<CommentDetails>(
      CompanyApiService.COMPANY_COMMENT_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId),
      payload,
      {params: {groupId}}
    );
  }

  public deleteComment(companyId: string, commentId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_COMMENT_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId),
      {params: {groupId}}
    );
  }

  public getCommentDocuments(companyId: string, commentId: string, groupId: string): Observable<CrmDocumentSummary[]> {
    return this.http.get<CrmDocumentSummary[]>(
      CompanyApiService.COMPANY_COMMENT_DOCUMENTS_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId),
      {params: {groupId}}
    );
  }

  public uploadCommentDocument(companyId: string, commentId: string, groupId: string, file: File): Observable<CrmDocumentSummary> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<CrmDocumentSummary>(
      CompanyApiService.COMPANY_COMMENT_DOCUMENTS_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId),
      formData,
      {params: {groupId}}
    );
  }

  public downloadCommentDocument(companyId: string, commentId: string, documentId: string, groupId: string): Observable<Blob> {
    return this.http.get(
      CompanyApiService.COMPANY_COMMENT_DOCUMENT_DOWNLOAD_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId)
        .replace('{documentId}', documentId),
      {params: {groupId}, responseType: 'blob'}
    );
  }

  public deleteCommentDocument(companyId: string, commentId: string, documentId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_COMMENT_DOCUMENT_URL
        .replace('{companyId}', companyId)
        .replace('{commentId}', commentId)
        .replace('{documentId}', documentId),
      {params: {groupId}}
    );
  }

  public getCompanyDocuments(companyId: string, groupId: string): Observable<CrmDocumentSummary[]> {
    return this.http.get<CrmDocumentSummary[]>(
      CompanyApiService.COMPANY_DOCUMENTS_URL.replace('{companyId}', companyId),
      {params: {groupId}}
    );
  }

  public uploadCompanyDocument(companyId: string, groupId: string, file: File): Observable<CrmDocumentSummary> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<CrmDocumentSummary>(
      CompanyApiService.COMPANY_DOCUMENTS_URL.replace('{companyId}', companyId),
      formData,
      {params: {groupId}}
    );
  }

  public downloadCompanyDocument(companyId: string, documentId: string, groupId: string): Observable<Blob> {
    return this.http.get(
      CompanyApiService.COMPANY_DOCUMENT_DOWNLOAD_URL
        .replace('{companyId}', companyId)
        .replace('{documentId}', documentId),
      {
        params: {groupId},
        responseType: 'blob'
      }
    );
  }

  public deleteCompanyDocument(companyId: string, documentId: string, groupId: string): Observable<void> {
    return this.http.delete<void>(
      CompanyApiService.COMPANY_DOCUMENT_URL
        .replace('{companyId}', companyId)
        .replace('{documentId}', documentId),
      {params: {groupId}}
    );
  }
}
